import React from 'react'
import { router, usePage, useForm } from '@inertiajs/react'
import {
  Box, Button, TextField, Select, MenuItem, FormControl, InputLabel,
  Paper, Typography, Grid, IconButton,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import AppShell from '../../../../components/layout/AppShell'
import PageHeader from '../../../../components/ui/PageHeader'
import FormattedNumber from '../../../../components/ui/FormattedNumber'

Form.layout = (page) => <AppShell>{page}</AppShell>

const KEY_TYPE_OPTIONS = [
  { value: 'attribute_based', label: 'Attribut-basiert' },
  { value: 'equal_per_unit', label: 'Gleich pro Einheit' },
  { value: 'composite', label: 'Komposit' },
  { value: 'direct', label: 'Direkt' },
  { value: 'custom', label: 'Individuell' },
]

export default function Form({ property, distribution_key, attribute_types = [], units = [], action, errors: serverErrors = {} }) {
  const { props } = usePage()
  const tenant = props.tenant || {}
  const basePath = `/m/${tenant.slug}/properties/${property.id}/distribution_keys`
  const isEdit = action === 'edit'

  const { data, setData, post, put, processing, errors } = useForm({
    name: distribution_key.name || '',
    key_type: distribution_key.key_type || 'attribute_based',
    unit_attribute_type_id: distribution_key.unit_attribute_type_id || '',
    components: distribution_key.components || [],
    target_unit_id: distribution_key.target_unit_id || '',
    custom_shares: distribution_key.custom_shares || [],
  })

  const allErrors = { ...serverErrors, ...errors }

  const handleSubmit = (e) => {
    e.preventDefault()
    if (isEdit) {
      put(`${basePath}/${distribution_key.id}`)
    } else {
      post(basePath)
    }
  }

  const addComponent = () => {
    setData('components', [...data.components, { unit_attribute_type_id: '', weight: '' }])
  }

  const removeComponent = (index) => {
    setData('components', data.components.filter((_, i) => i !== index))
  }

  const updateComponent = (index, field, value) => {
    const updated = [...data.components]
    updated[index] = { ...updated[index], [field]: value }
    setData('components', updated)
  }

  const componentWeightSum = data.components.reduce((sum, c) => sum + (parseFloat(c.weight) || 0), 0)

  return (
    <Box>
      <PageHeader
        title={isEdit ? 'Verteilerschlüssel bearbeiten' : 'Neuer Verteilerschlüssel'}
        breadcrumbs={[
          { label: 'Verteilerschlüssel', href: basePath },
          { label: isEdit ? 'Bearbeiten' : 'Neuer Schlüssel' },
        ]}
      />

      <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider', maxWidth: 600 }}>
        <Box component="form" onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <TextField label="Name" required fullWidth size="small"
                value={data.name} onChange={(e) => setData('name', e.target.value)}
                error={!!allErrors.name} helperText={allErrors.name?.[0]}
                slotProps={{ htmlInput: { 'data-testid': 'name-input' } }} />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <FormControl fullWidth size="small" required>
                <InputLabel>Typ</InputLabel>
                <Select value={data.key_type} label="Typ"
                  onChange={(e) => setData('key_type', e.target.value)}
                  data-testid="key-type-select">
                  {KEY_TYPE_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Attribute-based: select attribute type */}
            {data.key_type === 'attribute_based' && (
              <Grid size={{ xs: 12 }}>
                <FormControl fullWidth size="small" required error={!!allErrors.unit_attribute_type_id}>
                  <InputLabel>Attribut</InputLabel>
                  <Select value={data.unit_attribute_type_id} label="Attribut"
                    onChange={(e) => setData('unit_attribute_type_id', e.target.value)}
                    data-testid="attribute-type-select">
                    <MenuItem value="">Bitte wählen</MenuItem>
                    {attribute_types.map((at) => (
                      <MenuItem key={at.id} value={at.id}>{at.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            {/* Direct: select target unit */}
            {data.key_type === 'direct' && (
              <Grid size={{ xs: 12 }}>
                <FormControl fullWidth size="small" required>
                  <InputLabel>Ziel-Einheit</InputLabel>
                  <Select value={data.target_unit_id} label="Ziel-Einheit"
                    onChange={(e) => setData('target_unit_id', e.target.value)}
                    data-testid="target-unit-select">
                    <MenuItem value="">Bitte wählen</MenuItem>
                    {units.map((u) => (
                      <MenuItem key={u.id} value={u.id}>{u.number}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            {/* Composite: component list */}
            {data.key_type === 'composite' && (
              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle2" gutterBottom>Komponenten</Typography>
                {data.components.map((comp, idx) => (
                  <Box key={idx} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                    <FormControl size="small" sx={{ flex: 2 }}>
                      <InputLabel>Attribut</InputLabel>
                      <Select value={comp.unit_attribute_type_id} label="Attribut"
                        onChange={(e) => updateComponent(idx, 'unit_attribute_type_id', e.target.value)}
                        data-testid={`component-attr-${idx}`}>
                        {attribute_types.map((at) => (
                          <MenuItem key={at.id} value={at.id}>{at.name}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <TextField label="Gewichtung (%)" size="small" type="number"
                      value={comp.weight}
                      onChange={(e) => updateComponent(idx, 'weight', e.target.value)}
                      slotProps={{ htmlInput: { min: 0, max: 100, step: '0.1', 'data-testid': `component-weight-${idx}` } }}
                      sx={{ flex: 1 }} />
                    <IconButton size="small" onClick={() => removeComponent(idx)} data-testid={`remove-component-${idx}`}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                  <Button size="small" startIcon={<AddIcon />} onClick={addComponent}
                    data-testid="add-component-btn">
                    Komponente hinzufügen
                  </Button>
                  <Typography variant="body2" color={Math.abs(componentWeightSum - 100) < 0.01 ? 'success.main' : 'error.main'}>
                    Summe: <FormattedNumber value={componentWeightSum} decimals={1} percent align="left" />
                  </Typography>
                </Box>
                {allErrors.base && (
                  <Typography variant="body2" color="error" sx={{ mt: 1 }}>{allErrors.base[0]}</Typography>
                )}
              </Grid>
            )}
          </Grid>

          <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
            <Button type="submit" variant="contained" disabled={processing} data-testid="submit-btn">
              {processing ? 'Wird gespeichert...' : 'Speichern'}
            </Button>
            <Button variant="outlined" onClick={() => router.visit(basePath)}>
              Abbrechen
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  )
}
